import { NavLink, Link } from 'react-router-dom';
import { IconCalendar, IconClock, IconImages, IconMoon, IconPin, IconSun } from './Icon';
import { initials, useSession } from '../lib/session';
import { toggleTheme, useTheme } from '../lib/theme';

const DESTS = [
  { to: '/now', label: 'Now', Icon: IconClock },
  { to: '/schedule', label: 'Schedule', Icon: IconCalendar },
  { to: '/map', label: 'Map', Icon: IconPin },
  { to: '/gallery', label: 'Photos', Icon: IconImages },
];

/**
 * Sticky header: monogram, destinations, theme toggle, profile (D32).
 *
 * **Four destinations, and the fifth slot is deliberately empty.** The ceiling
 * is measured rather than assumed: at 375px five labelled tabs clear the
 * monogram and the avatar by 1.1px a side — they overflow their own flex box
 * into the header's gutter, which is why nothing visibly breaks and why there
 * is no room. A sixth wants 43px more than exists at 390px and 9px more at
 * 430px, the widest common phone; fitting six means 36.8px tabs, under the 44px
 * touch target. Three features have now been refused a slot on that arithmetic
 * (D54 the scorecard, D57 the playlist, D62 the icebreakers), and each went to
 * a surface inside a page instead.
 *
 * The icebreakers briefly held the fifth slot and gave it back: the deck reads
 * better inside the Schedule, where it replaced "Coming in", and two ways into
 * the same deck with two different interactions was worse than one good one.
 * Credits stayed in the footer, which is where it belongs regardless.
 *
 * The 375px breakpoint drops the labels below that width and keeps the icons.
 */
export default function Header() {
  const session = useSession();
  const { resolved } = useTheme();

  return (
    <header className="hdr">
      <Link className="hdr-brand" to="/now" aria-label="Meg and Videet — home">
        <span className="hdr-brand-short">M&amp;V</span>
      </Link>
      <nav className="hdr-dests" aria-label="Sections">
        {DESTS.map(({ to, label, Icon }) => (
          <NavLink key={to} to={to} className="hdr-dest">
            <Icon />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="hdr-right">
        <button
          type="button"
          className="hdr-icon-btn"
          onClick={toggleTheme}
          aria-label={resolved === 'dark' ? 'Switch to light' : 'Switch to dark'}
        >
          {resolved === 'dark' ? <IconMoon /> : <IconSun />}
        </button>
        <Link className="avatar" to="/me" aria-label="Your profile">
          {session ? initials(session.name) : '?'}
        </Link>
      </div>
    </header>
  );
}
